/**
 * useGeoImportPipeline — the shared client-side geo-import engine.
 *
 * Lifted verbatim from AddMapLayersDialog.processFiles so the Site Map "Add
 * layers" dialog and the Test-Fit Studio import dialog run the SAME plan →
 * execute → stage pipeline (KMZ / KML / GeoJSON / shapefiles / DWG through
 * the import router), including boundary-candidate surfacing, CAD row
 * pairing, per-plan provenance ids and warning collection. Hosts differ only
 * in what they DO with the staged rows.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { SiteLayer } from '../../types/site';
import { classifyLayerCategory } from '../../lib/siteParcelClassification';
import type { ParsedLayer } from '../../services/kmzParser';
import {
  executeGeoImportPlan,
  planGeoImports,
  type GeoImportPlan,
} from '../../services/geoImport/importRouter';
import { defaultSha256 } from '../../services/upload/uploadQueue';
import { sha256Hex } from '../../lib/reportGeneration/reportContractHash';

export interface GeoImportPlanProgress {
  plan: GeoImportPlan;
  status: 'pending' | 'parsing' | 'done' | 'error';
  error?: string;
}

export interface StagedGeoImport {
  rows: ParsedLayer[];
  /** Row index → prebuilt CAD SiteLayer (deterministic cad-* id preserved). */
  cadByRow: Map<number, SiteLayer>;
  /** Per-plan provenance ids, parallel to the plans that parsed. */
  provenance: Array<{ importId: string; files: File[] }>;
  warnings: string[];
  boundaryCandidateCount: number;
}

const sourceHintForPlanKind = (kind: GeoImportPlan['kind']): SiteLayer['source'] | undefined => {
  if (kind === 'geojson') return 'geojson-import';
  if (kind === 'shapefile-zip' || kind === 'shapefile-parts') return 'shapefile-import';
  return undefined; // kmz/kml keep the historical default; dwg rows arrive pre-stamped.
};

async function stableImportId(plan: GeoImportPlan): Promise<string> {
  const sourceHashes = await Promise.all(plan.files.map(file => defaultSha256(file)));
  sourceHashes.sort();
  return sha256Hex(JSON.stringify({ kind: plan.kind, sourceHashes }));
}

export interface GeoImportPipeline {
  progress: GeoImportPlanProgress[];
  rejections: string[];
  staged: StagedGeoImport | null;
  parsing: boolean;
  processFiles: (files: readonly File[]) => Promise<void>;
  reset: () => void;
}

export function useGeoImportPipeline(
  siteAnchor: { lat: number; lng: number } | null,
): GeoImportPipeline {
  const [progress, setProgress] = useState<GeoImportPlanProgress[]>([]);
  const [rejections, setRejections] = useState<string[]>([]);
  const [staged, setStaged] = useState<StagedGeoImport | null>(null);
  const [parsing, setParsing] = useState(false);
  const requestEpochRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    requestEpochRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setProgress([]);
    setRejections([]);
    setStaged(null);
    setParsing(false);
  }, []);

  useEffect(() => () => {
    requestEpochRef.current += 1;
    abortRef.current?.abort();
  }, []);

  const processFiles = useCallback(async (files: readonly File[]) => {
    if (files.length === 0) return;
    const requestEpoch = ++requestEpochRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestIsCurrent = (): boolean => (
      requestEpochRef.current === requestEpoch && !controller.signal.aborted
    );
    setParsing(true);
    setStaged(null);
    try {
      const { plans, rejected } = await planGeoImports(files);
      if (!requestIsCurrent()) return;
      setRejections(rejected.map((r) => `${r.file.name}: ${r.reason}`));
      const progressRows: GeoImportPlanProgress[] = plans.map((plan) => ({ plan, status: 'pending' }));
      setProgress(progressRows);
      if (plans.length === 0) {
        setParsing(false);
        return;
      }

      const rows: ParsedLayer[] = [];
      const cadByRow = new Map<number, SiteLayer>();
      const provenance: StagedGeoImport['provenance'] = [];
      const warnings: string[] = [];
      let boundaryCandidateCount = 0;

      for (let pi = 0; pi < plans.length; pi++) {
        const plan = plans[pi];
        if (!requestIsCurrent()) return;
        setProgress((prev) => prev.map((p, i) => (i === pi ? { ...p, status: 'parsing' } : p)));
        try {
          const execution = await executeGeoImportPlan(plan, {
            siteAnchor,
            signal: controller.signal,
          });
          if (!requestIsCurrent()) return;
          // File-content identity makes retry an upsert instead of a second
          // import transaction with unrelated UUID-derived feature ids.
          const importId = await stableImportId(plan);
          if (!requestIsCurrent()) return;
          provenance.push({ importId, files: plan.files });
          const stampRow = (layer: ParsedLayer): ParsedLayer => ({
            ...layer,
            attributes: {
              ...layer.attributes,
              importId,
              importFileName: plan.label,
            },
          });

          for (let li = 0; li < execution.parsed.layers.length; li++) {
            const rowIndex = rows.length;
            rows.push(stampRow(execution.parsed.layers[li]));
            const cadLayer = execution.cadLayers?.[li];
            if (cadLayer) cadByRow.set(rowIndex, cadLayer);
          }

          // Site-parcel candidates surface as ordinary rows on this shared
          // pipeline (boundary replacement stays with the boundary editor).
          const sourceHint = sourceHintForPlanKind(plan.kind);
          for (const cand of execution.parsed.boundaries) {
            boundaryCandidateCount++;
            rows.push(stampRow({
              name: cand.name,
              category: classifyLayerCategory(cand.name, cand.folderPath),
              geometry: { type: 'polygon', coordinates: cand.coordinates },
              folderPath: cand.folderPath,
              ...(sourceHint !== undefined ? { sourceHint } : {}),
            }));
          }

          for (const w of execution.parsed.warnings) warnings.push(`${plan.label}: ${w}`);
          setProgress((prev) => prev.map((p, i) => (i === pi ? { ...p, status: 'done' } : p)));
        } catch (e) {
          if (!requestIsCurrent()) return;
          const message = e instanceof Error ? e.message : String(e);
          warnings.push(`${plan.label}: ${message}`);
          setProgress((prev) => prev.map((p, i) => (i === pi ? { ...p, status: 'error', error: message } : p)));
        }
      }

      if (requestIsCurrent()) {
        setStaged({ rows, cadByRow, provenance, warnings, boundaryCandidateCount });
      }
    } finally {
      if (requestIsCurrent()) {
        abortRef.current = null;
        setParsing(false);
      }
    }
  }, [siteAnchor]);

  return { progress, rejections, staged, parsing, processFiles, reset };
}
